import type { ComponentProps } from 'react';

import { Separator, type SeparatorProps } from '@vs-shop/ui/separator';
import { Slider } from '@vs-shop/ui/slider';
import { clsxm } from '@vs-shop/utils';

/**
 * The range dimension of a product filter rail: a numeric facet bounded from
 * both ends, entered either by typing the two ends or by dragging them. Flat
 * named parts a tenant arranges in its own JSX, each painting itself.
 *
 *   <FilterRangeFacet>
 *     <FilterRangeBounds className="mb-4">
 *       <FilterRangeField>
 *         <FilterRangeInput value={lowText} onValueCommit={commitLow} … />
 *         <FilterRangeUnit>{currencySymbol}</FilterRangeUnit>
 *       </FilterRangeField>
 *
 *       <FilterRangeSeparator />
 *
 *       <FilterRangeField>
 *         <FilterRangeInput value={highText} onValueCommit={commitHigh} … />
 *         <FilterRangeUnit>{currencySymbol}</FilterRangeUnit>
 *       </FilterRangeField>
 *     </FilterRangeBounds>
 *
 *     <FilterRangeSlider
 *       min={0} max={100} step={0.01}
 *       value={[lowPosition, highPosition]}
 *       onValueChange={commitBoth}
 *       minLabel="Minimum"
 *       maxLabel="Maximum"
 *     />
 *   </FilterRangeFacet>
 *
 * Three things the range deliberately does NOT decide:
 *
 * - **What the numbers mean.** Every value crossing this seam is a plain
 *   number, and the box's own value is the string it displays. Money precision,
 *   step rounding and clamping are domain arithmetic that stays with the tenant
 *   holding the filter state — the same line the media contract draws when it
 *   says parts take final renderable URLs and the pipeline stays app-side.
 * - **Where a position on the track sits in the value range.** The track is a
 *   plain min/max/step scale; a tenant mapping a price curve or a discrete set
 *   of attribute values onto it does that on its own side and hands over
 *   positions.
 * - **What any of it says.** The unit suffix is children, and the two thumb
 *   names are props, because a range slider with two identically-named handles
 *   is unusable to a screen reader and no shared part can know the language.
 *
 * No `variant` anywhere: a range facet paints the same in every rail, and what
 * differs between rails is which parts are composed. The gap between the boxes
 * and the track is likewise the composition's, not a part's — it is space
 * BETWEEN parts, and the two are composed apart from each other in a rail that
 * wants only one of them.
 *
 * Server-first. The one part that must run on the client is the box itself,
 * which holds what is being typed; it is a separate leaf beside this file.
 */

/** The facet's body: its two bounds above its track. */
const filterRangeFacetClasses = 'w-full';

export type FilterRangeFacetProps = ComponentProps<'div'>;

/** One range facet's body. */
export const FilterRangeFacet = ({ className, ...props }: FilterRangeFacetProps) => (
  <div
    data-slot="filter-range-facet"
    className={clsxm(filterRangeFacetClasses, className)}
    {...props}
  />
);

/**
 * The two ends, pushed to the edges of the facet with whatever divides them
 * between.
 *
 * `justify-between` over exactly three children is safe here in a way the
 * spec's absent-part rule warns about elsewhere: a range has two ends by
 * definition, so neither box is ever the lone survivor whose place would
 * silently move.
 */
const filterRangeBoundsClasses = 'flex items-center justify-between';

export type FilterRangeBoundsProps = ComponentProps<'div'>;

/** The row holding both ends of the range. */
export const FilterRangeBounds = ({ className, ...props }: FilterRangeBoundsProps) => (
  <div
    data-slot="filter-range-bounds"
    className={clsxm(filterRangeBoundsClasses, className)}
    {...props}
  />
);

/**
 * One end's box: the positioning context its unit suffix hangs in.
 *
 * A fixed width rather than a share of the row, so the two ends stay the same
 * size as each other whatever they contain — a four-digit price must not make
 * its own box wider than the box beside it.
 */
const filterRangeFieldClasses = 'relative w-28';

export type FilterRangeFieldProps = ComponentProps<'div'>;

/** One end's box. */
export const FilterRangeField = ({ className, ...props }: FilterRangeFieldProps) => (
  <div
    data-slot="filter-range-field"
    className={clsxm(filterRangeFieldClasses, className)}
    {...props}
  />
);

/**
 * What the number is measured in, overlaid on the trailing end of the box.
 *
 * Overlaid rather than placed after the field, so the two ends keep their
 * width whether or not the facet has a unit at all. Nothing here can be
 * clicked or selected: the whole box is the field's click target, and dragging
 * across the number must not pick up the suffix with it.
 */
const filterRangeUnitClasses =
  'text-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-xs font-semibold select-none';

export type FilterRangeUnitProps = ComponentProps<'span'>;

/** The unit suffix inside one end's box. */
export const FilterRangeUnit = ({ className, ...props }: FilterRangeUnitProps) => (
  <span
    data-slot="filter-range-unit"
    className={clsxm(filterRangeUnitClasses, className)}
    {...props}
  />
);

export type FilterRangeSeparatorProps = Omit<SeparatorProps, 'orientation' | 'color'>;

/**
 * The dash between the two ends, reading as "from … to".
 *
 * Binds `@vs-shop/ui`'s `Separator` to the one appearance a range dash has,
 * which is why its two appearance props are not the caller's to set. It renders
 * no element of its own and so carries no `data-slot` of its own either — the
 * separator names the one element there is.
 */
export const FilterRangeSeparator = ({ className, ...props }: FilterRangeSeparatorProps) => (
  <Separator className={clsxm('bg-foreground mx-4 w-3 opacity-100', className)} {...props} />
);

const filterRangeSliderClasses = 'px-2';

export type FilterRangeSliderProps = Omit<
  ComponentProps<'div'>,
  'children' | 'defaultValue' | 'onChange'
> & {
  readonly min: number;
  readonly max: number;
  readonly step: number;
  /** Where the two thumbs sit on the `min`–`max` scale. */
  readonly value: readonly [number, number];
  readonly onValueChange: (value: readonly [number, number]) => void;
  /** The accessible name of the lower thumb. */
  readonly minLabel: string;
  /** The accessible name of the upper thumb. */
  readonly maxLabel: string;
};

/**
 * The track both ends are dragged along.
 *
 * The inset is the part's own and not the composition's: the thumbs are
 * centred on their value, so at either bound half a thumb hangs outside the
 * track and would be clipped by whatever the facet nests in. A composition
 * cannot see that it has to leave that room, which is what makes it structure
 * the part owns.
 *
 * The two thumbs are held one step apart, so the range can be collapsed to its
 * narrowest and still be two ends rather than one.
 */
export const FilterRangeSlider = ({
  className,
  min,
  max,
  step,
  value,
  onValueChange,
  minLabel,
  maxLabel,
  ...props
}: FilterRangeSliderProps) => (
  <div
    data-slot="filter-range-slider"
    className={clsxm(filterRangeSliderClasses, className)}
    {...props}
  >
    {/* Naming the value's shape is what makes the primitive report a pair
        back: left generic it would answer `number | readonly number[]`, and
        this part would have to narrow a shape it already knows. */}
    <Slider<readonly [number, number]>
      min={min}
      max={max}
      step={step}
      value={value}
      minStepsBetweenValues={1}
      getThumbAriaLabel={(index) => (index === 0 ? minLabel : maxLabel)}
      // One argument crosses, not two: the primitive also hands its callback a
      // Base UI event-details object, and a section is where a library detail
      // stops rather than something a tenant's handler has to know about.
      onValueChange={(next) => {
        onValueChange(next);
      }}
    />
  </div>
);
